import React, { FC, useCallback, useState } from 'react';
import { getThumbnailProps } from '../../../../utils/youtubeUtils';
import useMediaQuery from '../../../../lib/custom-hooks/useMediaQuery';
import { BreakpointsInPx } from '../../../../constants/breakpoints.constants';
import ModalDialog from '../../../@shared/modalDialog';
import OverlayCarouselPopup from '../../../@shared/overlayCarouselPopup';
import { TCardProps } from '../../types/cardColumnLayout.types';
import { CardModel } from './models/card.models';
import { getImageField } from '../../../../utils/getImageFields';
import { googleClickOpenVideoEvent } from '../../../../utils/analytics';
import CardButtons from './buttons';
import CardImage from './image';
import CardText from './text';
import SplitModule from '../../../splitModule';
import { getQueryLink } from '../../../../utils/getQuerylink';

const Card: FC<TCardProps> = ({ id, cardIndex, card, layout, isEE, triangle, cardHeadingTag }) => {
  const model = new CardModel(card);
  const isWide = useMediaQuery(`(min-width: ${BreakpointsInPx.md})`);
  const [showOverlay, setShowOverlay] = useState(false);
  const { thumbnailImgSrc } = getThumbnailProps(model.video?.value?.href || '');
  const closeOverlayModal = (): void => setShowOverlay(false);
  const [showSplitModule, setShowSplitModule] = useState(false);
  const handleSplitModuleModal = (state: boolean): void => setShowSplitModule(state);
  model.cta2 && getQueryLink(model.cta2)

  const showOverlayVideo = (): void => {
    if (!model.useVideo) return;
    setShowOverlay(true);
  };

  const imageOrThumbnail = getImageField(model.image, thumbnailImgSrc || '', model.useVideo);
  const mobileImageOrThumbnail = getImageField(model.mobileImage, thumbnailImgSrc || '', model.useVideo);

  const getOverlayProps = useCallback(() => {
    return [
      {
        id: id,
        desktopImage: imageOrThumbnail,
        mobileImage: mobileImageOrThumbnail,
        fullImage: imageOrThumbnail,
        desktopAlt: imageOrThumbnail?.value?.alt as string,
        mobileAlt: mobileImageOrThumbnail?.value?.alt as string,
        isVideo: model?.useVideo,
        videoUrl: model?.video?.value?.href,
        autoplay: true,
      },
    ];
  }, [id, imageOrThumbnail, mobileImageOrThumbnail, model]);

  return (
    <article className={'card'}>
      <CardImage
        imageOrThumbnail={imageOrThumbnail}
        mobileImageOrThumbnail={mobileImageOrThumbnail}
        isEE={isEE}
        useVideo={model.useVideo}
        showOverlayVideo={showOverlayVideo}
        id={id}
        layout={layout}
        isWide={isWide}
        triangle={triangle}
      />
      <div className={'card__content'}>
        <CardText title={model.title} subheading={model.subheading} cardHeadingTag={cardHeadingTag} />
        <CardButtons
          isEE={isEE}
          cta1={model.cta1}
          cta2={model.cta2}
          title={model.title}
          cardIndex={cardIndex}
          isWide={isWide}
          handleSplitModuleModal={handleSplitModuleModal}
          showPopupWithSplitModule={model.showPopupWithSplitModule}
          splitModuleLabel={model.splitModuleLabel || ''}
        />
      </div>

      {model?.video && model?.useVideo && (
        <ModalDialog
          isOpen={showOverlay}
          close={closeOverlayModal}
          onAfterOpen={() => googleClickOpenVideoEvent(model.title?.value || '', cardIndex + 1)}
        >
          <OverlayCarouselPopup slides={getOverlayProps()} analyticsMainTitle={model.title?.value} editorial={true} />
        </ModalDialog>
      )}
      {model?.showPopupWithSplitModule && model?.splitModules && (
        <ModalDialog isOpen={showSplitModule} close={(): void => handleSplitModuleModal(false)} className="split-module-modal">
          {model.splitModules.map((splitModule, index) => (
            <SplitModule
              {...splitModule}
              key={splitModule?.id}
              params={{ imagePosition: index % 2 == 0 ? 'right' : 'left' }}
              extraClassName={'card-split-module'}
            />
          ))}
        </ModalDialog>
      )}
    </article>
  );
};

export default Card;
